import {
  BALANCE,
  calculateDamage,
  createInitialBoard,
  createRng,
  hasAvailableMoves,
  moveBoard,
  recoverStalledBoard,
  type BoardMerge,
  type BoardState,
  type DamageResult,
  type Direction,
  type SeededRng,
  type Transition,
} from '@2k48/shared';

export type LocalBattleStatus = 'playing' | 'ko';

export interface LocalBattleSnapshot {
  readonly board: BoardState;
  readonly score: number;
  readonly combo: number;
  readonly maxCombo: number;
  readonly maxTile: number;
  readonly playerHp: number;
  readonly opponentHp: number;
  readonly moves: number;
  readonly status: LocalBattleStatus;
}

export interface LocalMoveResult {
  readonly accepted: boolean;
  readonly previous: LocalBattleSnapshot;
  readonly snapshot: LocalBattleSnapshot;
  readonly transitions: readonly Transition[];
  readonly merges: readonly BoardMerge[];
  readonly damage: DamageResult | null;
  readonly stalledPenalty: number;
}

export interface LocalBattleOptions {
  seed?: string;
  board?: BoardState;
  playerHp?: number;
  opponentHp?: number;
  /** Solo uses the same board rules without producing versus damage. */
  combat?: boolean;
}

export class LocalBattle {
  private readonly random: SeededRng;
  private readonly combat: boolean;
  private current: LocalBattleSnapshot;

  constructor(options: LocalBattleOptions = {}) {
    this.random = createRng(options.seed ?? 'local-battle');
    this.combat = options.combat ?? true;
    const board = options.board ?? createInitialBoard(this.random);
    this.current = {
      board,
      score: 0,
      combo: 0,
      maxCombo: 0,
      maxTile: highestTile(board),
      playerHp: clampHp(options.playerHp ?? BALANCE.versus.startingHp),
      opponentHp: clampHp(options.opponentHp ?? BALANCE.versus.startingHp),
      moves: 0,
      status: 'playing',
    };
  }

  get snapshot(): LocalBattleSnapshot {
    return this.current;
  }

  move(direction: Direction): LocalMoveResult {
    const previous = this.current;
    if (previous.status !== 'playing') return rejected(previous);

    const move = moveBoard(previous.board, direction, this.random);
    if (!move.moved) return rejected(previous);

    const damage = calculateDamage(
      move.merges.map((merge) => merge.value),
      previous.combo,
    );
    let board = move.state;
    let transitions = [...move.transitions];
    let stalledPenalty = 0;

    if (!hasAvailableMoves(board)) {
      const recovery = recoverStalledBoard(board);
      board = recovery.state;
      stalledPenalty = recovery.hpPenalty;
      transitions = [
        ...transitions,
        ...recovery.transitions.map((transition) => ({ ...transition, stage: 2 })),
      ];
    }

    const opponentHp = this.combat
      ? clampHp(previous.opponentHp - damage.damage)
      : previous.opponentHp;
    const playerHp = clampHp(previous.playerHp - stalledPenalty);
    const combo = damage.combo;
    this.current = {
      board,
      score: previous.score + move.scoreGain,
      combo,
      maxCombo: Math.max(previous.maxCombo, combo),
      maxTile: Math.max(previous.maxTile, highestTile(board)),
      playerHp,
      opponentHp,
      moves: previous.moves + 1,
      status: opponentHp === 0 || playerHp === 0 ? 'ko' : 'playing',
    };

    return {
      accepted: true,
      previous,
      snapshot: this.current,
      transitions,
      merges: move.merges,
      damage,
      stalledPenalty,
    };
  }

  damageOpponent(amount: number): { previous: LocalBattleSnapshot; snapshot: LocalBattleSnapshot } {
    return this.applyDirectDamage('opponent', amount);
  }

  damagePlayer(amount: number): { previous: LocalBattleSnapshot; snapshot: LocalBattleSnapshot } {
    return this.applyDirectDamage('player', amount);
  }

  private applyDirectDamage(
    target: 'player' | 'opponent',
    amount: number,
  ): { previous: LocalBattleSnapshot; snapshot: LocalBattleSnapshot } {
    const previous = this.current;
    const damage = Math.max(0, Math.floor(amount));
    const playerHp = target === 'player' ? clampHp(previous.playerHp - damage) : previous.playerHp;
    const opponentHp =
      target === 'opponent' ? clampHp(previous.opponentHp - damage) : previous.opponentHp;
    this.current = {
      ...previous,
      playerHp,
      opponentHp,
      status: playerHp === 0 || opponentHp === 0 ? 'ko' : previous.status,
    };
    return { previous, snapshot: this.current };
  }
}

function rejected(snapshot: LocalBattleSnapshot): LocalMoveResult {
  return {
    accepted: false,
    previous: snapshot,
    snapshot,
    transitions: [],
    merges: [],
    damage: null,
    stalledPenalty: 0,
  };
}

function highestTile(board: BoardState): number {
  return board.tiles.reduce((highest, tile) => Math.max(highest, tile.value), 0);
}

function clampHp(hp: number): number {
  return Math.min(BALANCE.versus.startingHp, Math.max(0, Math.round(hp)));
}
